import type { ReactElement } from "react";
import {
  canEquipInSlot,
  equipSlotOf,
  fittedMagazine,
  getItemDef,
  isTrueMagazine,
  repairCostEur,
  type Inventory,
  type InventoryOp,
  type ItemInstance,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { marketEntries } from "../meta/marketEntries.js";
import {
  openDialog,
  openWorkbench,
  setModWeapon,
  useHud,
  type OpenContainer,
} from "../store.js";
import { detachTargetFor } from "./modLogic.js";
import { FONT, MENU_BG, PANEL_LINE } from "../theme.js";
import {
  firstFreeSpot,
  instanceAt,
  uiViewportSize,
  unequipSpot,
  viewportToUi,
  type ItemSource,
} from "./drag.js";
import { quickMove } from "./quickmove.js";

/**
 * The right-click menu — the SHORTCUT for drags that are all wrist and no
 * decision. Entries that cannot apply are ABSENT, not disabled; the reason is
 * always visible in the inventory itself. A body slot gets the shortest menu:
 * look at it, take it.
 */

export interface MenuState {
  /** Viewport pixels — where the pointer was when the menu was asked for. */
  px: number;
  py: number;
  iid: string;
  source: ItemSource;
}

/**
 * Fixed width so the menu clamps to the viewport without measuring. 136 fits
 * `Repair (420 €)` at 12 px monospace with padding.
 */
const MENU_W = 136;
/** Above tile labels, drag ghosts (40), and tooltips (60). */
const MENU_BACKDROP_Z = 70;
const MENU_SURFACE_Z = MENU_BACKDROP_Z + 1;

/**
 * Which screen the menu opened on. `drop` and `useMed` are raid-only and would
 * bounce off the server in the menu, so they are absent there.
 */
export type MenuPlace = "raid" | "menu";

export interface ContextMenuProps {
  menu: MenuState;
  inv: Inventory;
  container: OpenContainer | null;
  /** Defaults to "raid" — the screen this menu was written for. */
  place?: MenuPlace;
  /**
   * Verbs THIS SCREEN adds, as a callback so the host can word them from the
   * item ("Sell · €123") and leave them out. Land last, where `Discard` sits.
   */
  extraEntries?: (item: ItemInstance, source: ItemSource) => readonly MenuEntry[];
  onClose: () => void;
}

/** One line of the menu: what it says, and what it does. */
export interface MenuEntry {
  label: string;
  run: () => void;
}

export function ContextMenu(props: ContextMenuProps): ReactElement | null {
  const entries = buildEntries(
    props.menu,
    props.inv,
    props.container,
    props.place ?? "raid",
    props.extraEntries,
  );
  // Called, not mounted, so the menu stays one element and tests find the
  // backdrop and surface where they always were.
  return MenuSurface({ px: props.menu.px, py: props.menu.py, entries, onClose: props.onClose });
}

/**
 * The menu as a shape — backdrop, clamped surface, one button per entry — for
 * screens whose menu is not about an inventory instance (the trader's shelf).
 */
export function MenuSurface(props: {
  /** Viewport pixels — where the pointer was when the menu was asked for. */
  px: number;
  py: number;
  entries: readonly MenuEntry[];
  onClose: () => void;
}): ReactElement | null {
  const entries = props.entries;
  if (entries.length === 0) return null;

  const point = viewportToUi(props.px, props.py);
  const viewport = uiViewportSize();
  const left = Math.min(point.x, Math.max(0, viewport.width - MENU_W - 8));
  const top = Math.min(point.y, Math.max(0, viewport.height - entries.length * 24 - 16));

  return (
    <>
      {/* Any click anywhere else dismisses the menu — including the click that
          was meant for a tile, which is the convention every OS menu follows. */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: MENU_BACKDROP_Z,
          pointerEvents: "auto",
        }}
        onPointerDown={props.onClose}
        onContextMenu={(ev) => {
          ev.preventDefault();
          props.onClose();
        }}
      />
      <div
        onPointerDown={(ev) => {
          // The inventory root dismisses the menu on pointerdown; a press
          // inside
          // must not unmount the button before its click lands.
          ev.stopPropagation();
        }}
        style={{
          position: "fixed",
          zIndex: MENU_SURFACE_Z,
          left,
          top,
          width: MENU_W,
          padding: 3,
          font: FONT,
          background: MENU_BG,
          border: `1px solid ${PANEL_LINE}`,
          borderRadius: 4,
          boxShadow: "0 6px 18px rgba(0, 0, 0, 0.55)",
          pointerEvents: "auto",
        }}
      >
        {entries.map((entry) => (
          <button
            key={entry.label}
            type="button"
            className="invmenu-item"
            onClick={() => {
              entry.run();
              props.onClose();
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** The applicable actions, in the order a player reaches for them. */
function buildEntries(
  menu: MenuState,
  inv: Inventory,
  container: OpenContainer | null,
  place: MenuPlace,
  extraEntries: ContextMenuProps["extraEntries"],
): MenuEntry[] {
  const item = instanceAt(inv, container, menu.source, menu.iid);
  if (item === null) return [];
  const def = getItemDef(item.defId);
  if (def === null) return [];

  const iid = menu.iid;
  const op = (o: InventoryOp) => () => {
    uiActions().tryOp(o);
  };
  const entries: MenuEntry[] = [];

  // --- Inspect: on every item, everywhere. A GUN opens the Werkbank (read-only
  // at the trader), everything else the detail dialog. Dialogs stack.
  // "Own" is the word doing the work below: a body slot and a corpse's pockets
  // are reachable but not the player's own. Unload cares; Inspect does not.
  const ownGrid = menu.source.t === "grid" || menu.source.t === "box";
  const ownWeapon =
    menu.source.t === "slot" || menu.source.t === "bodySlot"
      ? menu.source.slot === "pistol" || menu.source.slot === "weapon" || menu.source.slot === "weapon2"
      : ownGrid;
  const benchable = def.kind === "weapon";
  entries.push({
    label: "Inspect",
    run: () => {
      if (benchable) setModWeapon(iid);
      else openDialog("inspect", iid);
    },
  });

  // --- Modify: the fullscreen workbench, between raids only — it lives on the
  // Gear page, and there is no bench in a raid. Only for a gun the player can
  // reach as an ITEM (a grid, a box, a worn slot); a corpse's rifle is not
  // theirs to rebuild.
  if (
    place === "menu" &&
    def.kind === "weapon" &&
    (menu.source.t === "grid" || menu.source.t === "slot" || menu.source.t === "box")
  ) {
    entries.push({
      label: "Modify",
      run: () => {
        openWorkbench(iid);
      },
    });
  }

  // --- Market search, between raids only (the entries gate themselves).
  if (place === "menu") entries.push(...marketEntries(def.id));

  // --- Take (body slot): `quickMove`, so it answers like Ctrl+click — own
  // grids in canonical order, then the first equipment slot that takes it.
  if (menu.source.t === "bodySlot") {
    const source = menu.source;
    entries.push({
      label: "Take",
      run: () => {
        quickMove(source, iid);
      },
    });
    return entries;
  }

  // --- Open: any container in any grid, everywhere, empty or not. A worn
  // rig/backpack opens its hoisted grid (also what double-clicking the slot
  // does).
  if (
    def.kind === "container" &&
    (menu.source.t === "grid" ||
      menu.source.t === "box" ||
      (menu.source.t === "slot" &&
        (menu.source.slot === "rig" || menu.source.slot === "backpack")))
  ) {
    entries.push({
      label: "Open",
      run: () => {
        openDialog("box", iid);
      },
    });
  }

  // --- Use: meds, from the player's OWN grids only (`useMed` searches the
  // inventory, not the open container). The screen stays open.
  if (
    place === "raid" &&
    menu.source.t === "grid" &&
    menu.source.area !== "container" &&
    def.kind === "med"
  ) {
    const charges = item.durability ?? def.charges;
    if (charges > 0) {
      entries.push({
        label: "Use",
        run: () => {
          uiActions().useMed(iid);
        },
      });
    }
  }

  // --- Eat and drink: the same message (`useMed`) and the same gate. A grocery
  // is `kind: "loot"`, so `consume` marks it edible. "Drink" for hydration-only
  // items — thirst and hunger are different meters.
  if (
    place === "raid" &&
    menu.source.t === "grid" &&
    menu.source.area !== "container" &&
    def.kind === "loot" &&
    def.consume !== undefined
  ) {
    const c = def.consume;
    entries.push({
      label: c.energy <= 0 && c.hydration > 0 ? "Drink" : "Eat",
      run: () => {
        uiActions().useMed(iid);
      },
    });
  }

  // --- Unload. On the GUN the magazine comes out (a `detach`); on a LOOSE
  // MAGAZINE the rounds pour out as stacks (gated on there being any). Raid AND
  // menu. The landing spot is `detachTargetFor` — never somebody else's crate.
  if (def.kind === "weapon" && ownWeapon) {
    const fitted = fittedMagazine(def, item);
    const to =
      fitted === null
        ? null
        : detachTargetFor(inv, fitted.def, place === "menu" ? container : null);
    if (to !== null) {
      entries.push({ label: "Unload", run: op({ t: "detach", weaponIid: iid, slot: "mag", to }) });
    }
  } else if (isTrueMagazine(def) && ownGrid && (item.mag ?? 0) > 0) {
    entries.push({ label: "Unload", run: op({ t: "unload", iid }) });
  }

  // --- Repair: plates and guns, menu only, through `repairCostEur`. From ANY
  // source (a repair destroys nothing). Absent when at ceiling or unaffordable.
  // The one entry that names its price, since the cost is not visible
  // elsewhere.
  if (place === "menu" && (def.kind === "armor" || def.kind === "weapon")) {
    const cost = repairCostEur(item);
    // Read non-reactively: `buildEntries` is a plain function, and a menu lives
    // for one gesture.
    const euros = useHud.getState().profile?.euros ?? null;
    if (cost !== null && euros !== null && euros >= cost) {
      entries.push({
        label: `Repair (${cost} €)`,
        run: () => {
          uiActions().repair(iid);
        },
      });
    }
  }

  // --- Equip: a box counts as a grid (`equip` finds sources through nested
  // grids).
  if (menu.source.t === "grid" || menu.source.t === "box") {
    const declared = equipSlotOf(def);
    // A gun goes in the first slot it fits AND finds free — offering a slot the
    // def cannot use spends an op on "wrong-slot".
    const slot =
      def.kind === "weapon"
        ? (["weapon", "weapon2", "pistol"] as const).find(
            (s) => canEquipInSlot(def, s) && inv.equipment[s] === null,
          ) ?? null
        : declared;
    if (slot !== null && inv.equipment[slot] === null) {
      entries.push({ label: "Equip", run: op({ t: "equip", iid, slot }) });
    }
  }

  // --- Unequip: the destination is resolved HERE in canonical order; no room
  // means no entry. A full bag comes off wrapped, so only its own footprint
  // matters.
  if (menu.source.t === "slot") {
    const slot = menu.source.slot;
    const to = unequipSpot(inv, container, slot, def);
    if (to !== null) entries.push({ label: "Unequip", run: op({ t: "unequip", slot, to }) });
  }

  // --- Split: half, into the SAME grid if there is room, else the player's own
  // grids (a stack inside a box goes to the grids — `firstFreeSpot` speaks in
  // areas).
  const splitSource = menu.source;
  if (
    (splitSource.t === "grid" || splitSource.t === "box") &&
    def.stackMax > 1 &&
    item.stack >= 2
  ) {
    const to = firstFreeSpot(
      inv,
      container,
      def,
      splitSource.t === "grid" ? splitSource.area : undefined,
    );
    if (to !== null) {
      entries.push({
        label: "Split",
        run: op({ t: "split", iid, count: Math.floor(item.stack / 2), to }),
      });
    }
  }

  // --- Discard: raid only, grid and worn items alike (a full rig's refusal is
  // the shared rules'). Not for a fitted part — `drop` wants a grid item.
  if (place === "raid" && menu.source.t !== "mod") {
    entries.push({ label: "Discard", run: op({ t: "drop", iid }) });
  }

  // --- Whatever THIS screen does with an item, last. See `extraEntries`.
  if (extraEntries !== undefined) entries.push(...extraEntries(item, menu.source));

  return entries;
}
